import {
    AnalyticsEvent,
    generateInsertId,
    PUSH_NOTIFICATION_FAILURE,
    PUSH_NOTIFICATION_SUCCESS,
} from '../common/Analytics';
import { ErrorReporter } from '../common/ErrorReporter';
import type { FCMProps, PushNotificationResponse } from '../common/PushNotifications';
import type DB from '../db/DB';
import type { FCMPayload, PostFCMFn } from './ServerReplicantFCM';

export type SendAndroidPushNotificationFn = (opts: {
    receiverId: string;
    androidDeviceToken: string;
    notification: FCMProps;
    payload?: FCMPayload;
    userSessionId: string;
    analyticsEventProperties: { [key: string]: unknown };
    analyticsUserProperties?: { [key: string]: unknown };
}) => Promise<PushNotificationResponse>;

export default class ServerReplicantAndroidPushNotifications {
    constructor(
        private opts: {
            db: DB<any>;
            postFirebaseCloudMessage: PostFCMFn;
            sendAnalyticsEvents: (events: AnalyticsEvent[]) => Promise<void>;
            dateNow: () => number;
            errorReporter: ErrorReporter;
        },
    ) {}

    sendAndroidPushNotification: SendAndroidPushNotificationFn = async (opts) => {
        const {
            receiverId,
            androidDeviceToken,
            notification,
            payload,
            analyticsEventProperties,
            analyticsUserProperties,
            userSessionId,
        } = opts;

        const result = await this.opts.postFirebaseCloudMessage(androidDeviceToken, notification, payload);

        const analyticsEvents: AnalyticsEvent[] = [];

        if (result.success) {
            await this.opts.db.chatbot
                .updateTimeLastTargetedByChannelMessage(receiverId, 'android', this.opts.dateNow())
                .catch(() => undefined);

            const successAnalyticsEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: PUSH_NOTIFICATION_SUCCESS,
                event_properties: {
                    ...analyticsEventProperties,
                    platform: 'ANDROID_APP',
                },
                user_properties: analyticsUserProperties,
                insert_id: generateInsertId(receiverId, userSessionId),
            };
            analyticsEvents.push(successAnalyticsEvent);
        } else {
            const failureAnalyticsEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: PUSH_NOTIFICATION_FAILURE,
                event_properties: {
                    ...analyticsEventProperties,
                    reason: result.error,
                    platform: 'ANDROID_APP',
                },
                insert_id: generateInsertId(receiverId, userSessionId),
            };
            analyticsEvents.push(failureAnalyticsEvent);

            // Handle Firebase unsubs
            if (result.isUnsubscribed) {
                await this.opts.db.chatbot.setAndroidDeviceToken(receiverId, '', 0);
            } else {
                await this.opts.errorReporter.captureException(result.error, undefined, opts);
            }
        }

        await this.opts.sendAnalyticsEvents(analyticsEvents);

        return { success: result.success, reason: result.success ? undefined : result.error.message };
    };
}
